import React, { useState } from 'react';
import { Shield, Lock, Eye, CheckCircle, Smartphone, Mail, Server, ArrowRight } from 'lucide-react';
import { hiringService } from '../lib/services'; // Assumption: needs update
import type { MarketplaceReplica } from '../types/api';

interface OnboardingWizardProps {
    contractId: string;
    replica: MarketplaceReplica;
    onComplete: () => void;
}

export default function OnboardingWizard({ contractId, replica, onComplete }: OnboardingWizardProps) {
    const [step, setStep] = useState(1);
    const [isProcessing, setIsProcessing] = useState(false);
    const [credentials, setCredentials] = useState<{ email?: string; phone?: string }>({});

    // Step 2: Lock Scope
    const [scopeLocked, setScopeLocked] = useState(false);
    const [scopeHash, setScopeHash] = useState('');

    // Step 5: Shadow Mode result
    const [shadowResult, setShadowResult] = useState('');

    const handleLockScope = async () => {
        setIsProcessing(true);
        // Mock API call
        // const res = await hiringService.lockRole(contractId, { role: 'Standard' });
        setTimeout(() => {
            setScopeHash('0x' + Math.random().toString(16).substr(2, 40));
            setScopeLocked(true);
            setIsProcessing(false);
        }, 1500);
    };

    const handleActivateShadow = async () => {
        setIsProcessing(true);
        // Mock API call
        setTimeout(() => {
            setShadowResult('Shadow Mode Active: Replica is processing data safely.');
            setIsProcessing(false);
        }, 1500);
    };

    const handleGoLive = async () => {
        setIsProcessing(true);
        // Mock API call
        setTimeout(() => {
            setCredentials({
                email: `replica.${replica.id.substring(0, 4)}@company.com`,
                phone: '+1-555-0123'
            });
            setIsProcessing(false);
            setStep(5); // Completion
        }, 2000);
    };

    const nextStep = () => setStep(s => s + 1);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
            <div className="w-full max-w-4xl bg-slate-900 rounded-2xl border border-slate-700 overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-slate-700 flex justify-between items-center bg-slate-800">
                    <div>
                        <h2 className="text-2xl font-bold text-white">Employee Onboarding</h2>
                        <p className="text-slate-400">Positioning {replica.name} for your team</p>
                    </div>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4, 5].map(i => (
                            <div key={i} className={`h-2 w-12 rounded-full ${i <= step ? 'bg-sky-500' : 'bg-slate-700'}`} />
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 p-8 overflow-y-auto">

                    {step === 1 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-sky-500/10 rounded-full text-sky-400">
                                    <Lock size={32} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">Step 2: Lock the Role</h3>
                                    <p className="text-slate-400">Define strict boundaries. Once signed, the agent cannot exceed this scope.</p>
                                </div>
                            </div>

                            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 font-mono text-sm text-slate-300">
                                <p className="mb-2 text-sky-400">// ROLE DEFINITION</p>
                                <p>Role: {replica.category}</p>
                                <p>Permissions: Read-Only (CRM), Write (Drafts)</p>
                                <p>Forbidden: Financial Transactions, PII Export</p>
                                <p className="mt-4 text-slate-500">Hash: {scopeHash || 'Not generated'}</p>
                            </div>

                            <button
                                onClick={handleLockScope}
                                disabled={scopeLocked || isProcessing}
                                className={`w-full py-4 rounded-xl font-bold transition-all ${scopeLocked ? 'bg-green-500/20 text-green-400' : 'bg-sky-500 hover:bg-sky-400 text-white'}`}
                            >
                                {isProcessing ? 'Cryptographically Signing...' : scopeLocked ? 'Role Locked & Signed' : 'Sign & Lock Scope'}
                            </button>

                            {scopeLocked && (
                                <button onClick={nextStep} className="w-full flex justify-end text-sky-400 items-center gap-2 hover:text-sky-300">
                                    Next: Assign Guardian <ArrowRight size={18} />
                                </button>
                            )}
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-purple-500/10 rounded-full text-purple-400">
                                    <Shield size={32} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">Step 3: Assign Decision Guardian</h3>
                                    <p className="text-slate-400">Who is the human accountable for this agent?</p>
                                </div>
                            </div>

                            <div className="bg-slate-800 p-4 rounded-xl border border-slate-700 flex items-center justify-between hover:border-purple-500 cursor-pointer transition-colors">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-slate-600 flex items-center justify-center font-bold">ME</div>
                                    <div>
                                        <p className="font-medium text-white">Assign to Me</p>
                                        <p className="text-sm text-slate-400">I will handle escalations</p>
                                    </div>
                                </div>
                                <CheckCircle className="text-purple-500" />
                            </div>

                            <button onClick={nextStep} className="w-full py-4 bg-slate-700 hover:bg-slate-600 rounded-xl text-white font-medium">
                                Confirm Assignment
                            </button>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-amber-500/10 rounded-full text-amber-400">
                                    <Eye size={32} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">Step 5: Shadow Mode</h3>
                                    <p className="text-slate-400">Run the agent safely. Outputs are generated but suppressed.</p>
                                </div>
                            </div>

                            <div className="bg-black p-6 rounded-xl border border-slate-700 font-mono text-xs text-green-400 h-48 overflow-y-auto">
                                <p>{'>'} Initializing Shadow Protocol...</p>
                                {shadowResult && <p>{'>'} {shadowResult}</p>}
                                {shadowResult && <p>{'>'} Test plan generated. 0 side effects produced.</p>}
                            </div>

                            <button
                                onClick={handleActivateShadow}
                                disabled={Boolean(shadowResult) || isProcessing}
                                className="w-full py-4 bg-amber-600 hover:bg-amber-500 rounded-xl text-white font-bold"
                            >
                                {isProcessing ? 'Initializing...' : shadowResult ? 'Shadow Mode Verified' : 'Activate Shadow Mode'}
                            </button>

                            {shadowResult && (
                                <button onClick={nextStep} className="w-full flex justify-end text-amber-400 items-center gap-2 hover:text-amber-300 mt-4">
                                    Next: Go Live <ArrowRight size={18} />
                                </button>
                            )}
                        </div>
                    )}

                    {step === 4 && (
                        <div className="space-y-6">
                            <div className="flex items-center gap-4 mb-8">
                                <div className="p-4 bg-green-500/10 rounded-full text-green-400">
                                    <CheckCircle size={32} />
                                </div>
                                <div>
                                    <h3 className="text-xl font-semibold text-white">Step 6: Activate & Provision</h3>
                                    <p className="text-slate-400">Grant access and generate corporate credentials.</p>
                                </div>
                            </div>

                            <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                                <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                                    <span className="text-slate-300 flex items-center gap-2"><Mail size={16} /> Email Provisioning</span>
                                    <span className="text-green-400 text-sm">Ready</span>
                                </div>
                                <div className="p-4 flex items-center justify-between">
                                    <span className="text-slate-300 flex items-center gap-2"><Smartphone size={16} /> Phone Line</span>
                                    <span className="text-green-400 text-sm">Ready</span>
                                </div>
                            </div>

                            <button
                                onClick={handleGoLive}
                                disabled={isProcessing}
                                className="w-full py-4 bg-green-600 hover:bg-green-500 rounded-xl text-white font-bold text-lg animate-pulse"
                            >
                                {isProcessing ? 'Provisioning...' : 'ACTIVATE REPLICA'}
                            </button>
                        </div>
                    )}

                    {step === 5 && (
                        <div className="text-center space-y-6 py-10">
                            <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-500/50">
                                <CheckCircle size={48} className="text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-white">Hiring Complete!</h2>
                            <p className="text-slate-400 max-w-md mx-auto">
                                {replica.name} is now an active member of your team.
                            </p>

                            <div className="bg-slate-800/50 p-6 rounded-xl border border-slate-700 max-w-md mx-auto text-left">
                                <div className="flex items-center gap-3 mb-3">
                                    <Mail className="text-sky-400" size={20} />
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase">Work Email</p>
                                        <p className="text-white select-all">{credentials.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Smartphone className="text-green-400" size={20} />
                                    <div>
                                        <p className="text-xs text-slate-500 uppercase">Direct Line</p>
                                        <p className="text-white select-all">{credentials.phone}</p>
                                    </div>
                                </div>
                            </div>

                            <button onClick={onComplete} className="px-8 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-white font-medium">
                                Go to Dashboard
                            </button>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}
